import MaintenanceResource from '@/api/resources/maintenance'
import MaintenanceCommentResource from '@/api/resources/maintenanceComment'
import getCurrentCompanyId from '@/api/company.service'
import { IMaintenancePages } from '@/interfaces/components/dataTableMaintenances/maintenance-pages'
import { IMaintenance } from '@/interfaces/entities/maintenance'
import { IMaintenanceType } from '@/interfaces/entities/maintenance_type'
import { defineStore } from 'pinia'
import { useLoadingStore } from './loading'
import { ISensor } from '@/interfaces/entities/sensor'
import { IUser } from '@/interfaces/entities/user'
import {
  IArchiveMaintenance,
  ICreateMaintenance,
  ICreateMaintenanceType,
  IMaintenanceReport,
  IUpdateMaintenance,
} from '@/interfaces/api/maintenance'
import { AnalyticsEvents, logFirebaseAnalytics } from '@/utils/firebase'
import { useErrorStore } from './error'
import { useSuccessStore } from './success'
import { ICreateMaintenanceComment, IUpdateMaintenanceComment } from '@/interfaces/api/maintenanceComment'
import { IMaintenanceComment } from '@/interfaces/entities/maintenance_comment'

export const useMaintenanceStore = defineStore({
  id: 'maintenance',
  persist: {
    paths: [
      'maintenances',
      'maintenancesUser',
      'maintenanceTypes',
      'MaintenanceComments',
      'currentSensorFilter',
      'currentStatusFilter',
      'currentOrigimFilter',
      'currentPriorityFilter',
      'currentDescriptionFilter',
      'currentActionsFilter',
      'currentStartDateFilter',
      'currentEndDateFilter',
      'currentMaintenanceTypeFilter',
      'currentUserFilter',
      'pagination',
    ],
  },
  state: () => ({
    maintenances: [] as IMaintenancePages['data'],
    maintenancesUser: [] as IMaintenancePages['data'],
    maintenanceTypes: [] as IMaintenanceType[],
    MaintenanceComments : [] as IMaintenanceComment[],
    currentMaintenance: null as IMaintenance | null,
    currentSensorFilter: {} as ISensor | null,
    currentStatusFilter: '' as {} | null,
    currentOrigimFilter: '' as {} | null,
    currentPriorityFilter: '' as {} | null,
    currentDescriptionFilter: '' as string | null,
    currentActionsFilter: '' as string | null,
    currentStartDateFilter: '' as string | null,
    currentEndDateFilter: '' as string | null,
    currentMaintenanceTypeFilter: {} as IMaintenanceType | null,
    currentUserFilter: {} as IUser | null,
    pagination: null as IMaintenancePages['pagy'] | null,
    maintenanceReport: null as IMaintenanceReport | null,
    maintenanceReportLoaded: false,
  }),
  getters: {
    getMaintenances: (state) => state.maintenances,
    listUserMaintenances: (state) => state.maintenancesUser,
    listMaintenanceTypes: (state) => state.maintenanceTypes,
    listMaintenanceComments: (state) => state.MaintenanceComments,
    getCurrentMaintenance: (state) => state.currentMaintenance,
    getPagination: (state) => state.pagination,
    getCurrentSensorFilter: (state) => state.currentSensorFilter,
    getCurrentUserFilter: (state) => state.currentUserFilter,
    getCurrentStatusFilter: (state) => state.currentStatusFilter,
    getCurrentOrigimFilter: (state) => state.currentOrigimFilter,
    getCurrentPriorityFilter: (state) => state.currentPriorityFilter,
    getCurrentMaintenanceTypeFilter: (state) => state.currentMaintenanceTypeFilter,
    getMaintenanceReport: (state) => state.maintenanceReport,
    hasMaintenanceReportLoaded: (state) => state.maintenanceReportLoaded,
  },
  actions: {
    async getMaintenancesPages(page: number, items: number, sort: string, sort_direction: string) {
      const loadingStore = useLoadingStore()
      const sensor_id = this.currentSensorFilter?.id ?? ''
      const user_id = this.currentUserFilter?.id ?? ''
      const status = this.currentStatusFilter?.id ?? ''
      const origim = this.currentOrigimFilter?.id ?? ''
      const priority = this.currentPriorityFilter?.id ?? ''
      const description = this.currentDescriptionFilter ?? ''
      const actions_taken = this.currentActionsFilter ?? ''
      const start_date = this.currentStartDateFilter ?? ''
      const end_date = this.currentEndDateFilter ?? ''
      const maintenance_type = this.currentMaintenanceTypeFilter?.id ?? ''
      try {
        loadingStore.initLoading()

        const result = await MaintenanceResource.getMaintenancePages(
          page,
          items,
          sort,
          sort_direction,
          sensor_id,
          user_id,
          status,
          origim,
          priority,
          description,
          actions_taken,
          start_date,
          end_date,
          maintenance_type,
        )

        this.maintenances = result.data || result
        this.pagination = result.pagy || {
          count: result.length,
          prev: page > 1 ? page - 1 : null,
          next: result.length === items ? page + 1 : null,
          pages: Math.ceil(result.length / items) || 1,
        }
      } catch (error) {
      } finally {
        loadingStore.removeLoading()
      }
    },
    async getUserMaintenances(){
      const loadingStore = useLoadingStore()
      try{
        loadingStore.initLoading()
        const result = await MaintenanceResource.getUserMaintenance()
        this.maintenancesUser = result
      }
      catch(error){
      }
      finally{
        loadingStore.removeLoading()
      }
    },

    clearCurrentMaintenance() {
      this.currentMaintenance = null
    },

    async getMaintenanceById(maintenanceId: number) {
      const loadingStore = useLoadingStore()
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        loadingStore.initLoading()
        const result = await MaintenanceResource.getMaintenance(maintenanceId)
        this.currentMaintenance = result as IMaintenance
        return this.currentMaintenance
      } catch (error: unknown) {
        this.currentMaintenance = null
        const err = error as { response?: { data?: { errors?: string[] } } }
        errorStore.setErrors('Erro ao carregar manutenção: ', [
          err?.response?.data?.errors?.[0] || 'Falha ao carregar detalhes da manutenção',
        ])
        return null
      } finally {
        loadingStore.removeLoading()
      }
    },
    async getMaintenanceTypes() {
      const loadingStore = useLoadingStore()
      try {
        loadingStore.initLoading()
        const result = await MaintenanceResource.getMaintenanceTypes()
        this.maintenanceTypes = result
      } catch (error) {
      } finally {
        loadingStore.removeLoading()
      }
    },

    async getMaintenanceComments(page: number, items: number, sort: string, sort_direction: string, maintenance_id: string) {
      const loadingStore = useLoadingStore()
      try {
        loadingStore.initLoading()
        const result = await MaintenanceCommentResource.getMaintenanceCommentsPages(
          page,
          items,
          sort,
          sort_direction,
          maintenance_id,
        )
        return result
      } catch (error) {
      } finally {
        loadingStore.removeLoading()
      }
    },

    async createMaintenanceComment(newMaintenanceComment: ICreateMaintenanceComment) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        const createdMaintenanceComment = await MaintenanceCommentResource.createMaintenanceComment(newMaintenanceComment)
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.COMMENT.CREATED)
        useSuccessStore().setSuccess(['Comentario criado com sucesso!'])
      } catch (error) {
        errorStore.setErrors('Erro ao atualizar ao criar comentario: ', [error?.response?.data?.errors[0] || 'Falha ao criar comentario'])
      }
    },
    async createMaintenanceType(newMaintenanceType: ICreateMaintenanceType) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        const createdMaintenanceType = await MaintenanceResource.createMaintenanceType(newMaintenanceType)
        this.maintenanceTypes.push(createdMaintenanceType)
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.TYPE.CREATED)
        useSuccessStore().setSuccess(['Tipo de manutenção criado com sucesso!'])
      } catch (error) {
        errorStore.setErrors('Erro ao atualizar ao criar tipo de manutenção: ', [error?.response?.data?.errors[0] || 'Falha ao criar tipo de manutenção'])
      }
    },

    async createMaintenance(newMaintenance: ICreateMaintenance) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        const createdMaintenance = await MaintenanceResource.createMaintenance(newMaintenance)
        const sessionCompanyId = Number(getCurrentCompanyId())
        const ownershipCompanyId = Number(newMaintenance.allocated_company_id ?? sessionCompanyId)

        if (ownershipCompanyId === sessionCompanyId) {
          this.maintenances.push(createdMaintenance)
        }
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.CREATED)
        useSuccessStore().setSuccess(['Manutenção criada com sucesso!'])
      } catch (error) {
        errorStore.setErrors('Erro ao atualizar ao criar manutenção: ',[error?.response?.data?.errors[0] || 'Falha ao criar manutenção'])
      }
    },

    async updateMaintenanceComment(maintenance_comment_id: number, updateMaintenanceComment: IUpdateMaintenanceComment) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        const updatedMaintenanceComment = await MaintenanceCommentResource.updateMaintenanceComment(maintenance_comment_id, updateMaintenanceComment)
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.COMMENT.EDITED)
        useSuccessStore().setSuccess(['Comentario editado com sucesso!'])
      } catch (error) {
        errorStore.setErrors('Erro ao atualizar ao atualizar comentario: ',[error?.response?.data?.errors[0] || 'Falha ao editar comentario'])
      }
    },
    
    async updateMaintenance(maintenance_id: number, updateMaintenance: IUpdateMaintenance) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        const updatedMaintenance = await MaintenanceResource.updateMaintenance(maintenance_id, updateMaintenance)
        const idx = this.maintenancesUser.findIndex((m) => m.id === maintenance_id)
        if (idx >= 0) {
          this.maintenancesUser[idx] = updatedMaintenance
        }
        const idxList = this.maintenances.findIndex((m) => m.id === maintenance_id)
        if (idxList >= 0) {
          this.maintenances[idxList] = updatedMaintenance
        }
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.EDITED)
        useSuccessStore().setSuccess(['Manutenção editada com sucesso!'])
        return updatedMaintenance
      } catch (error) {
        errorStore.setErrors('Erro ao atualizar ao atualizar manutenção: ',[error?.response?.data?.errors[0] || 'Falha ao editar manutenção'])
      }
    },

    async deleteMaintenanceComment(maintenance_comment_id: number) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        await MaintenanceCommentResource.deleteMaintenanceComment(maintenance_comment_id)
        logFirebaseAnalytics(AnalyticsEvents.MAINTENANCE.COMMENT.DELETED)
        useSuccessStore().setSuccess(['Comentário deletado com sucesso!'])
      } catch (error) {
        errorStore.setErrors('Erro ao deletar comentário: ', [error?.response?.data?.errors?.[0] || 'Falha ao deletar comentário'])
      }
    },

    async destroyMaintenance(maintenanceId: number) {
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        await MaintenanceResource.deleteMaintenance(maintenanceId)
        this.maintenancesUser = this.maintenancesUser.filter((m) => m.id !== maintenanceId)
        useSuccessStore().setSuccess(['Manutenção excluída com sucesso!'])
      } catch (error: unknown) {
        const err = error as { response?: { data?: { errors?: string[] } } }
        errorStore.setErrors('Erro ao excluir manutenção: ', [err?.response?.data?.errors?.[0] || 'Falha ao excluir manutenção'])
      }
    },

    async archiveMaintenance(maintenanceId: number, params: IArchiveMaintenance = { archived: true }) {
      const errorStore = useErrorStore()
      const shouldArchive = params.archived !== false
      try {
        errorStore.clearErrors()
        const archivedMaintenance = (await MaintenanceResource.archiveMaintenance(
          maintenanceId,
          params,
        )) as IMaintenance

        if (shouldArchive) {
          this.maintenancesUser = this.maintenancesUser.filter((m) => m.id !== maintenanceId)
          this.maintenances = this.maintenances.filter((m) => m.id !== maintenanceId)
          useSuccessStore().setSuccess(['Manutenção arquivada com sucesso!'])
        } else {
          const idxUser = this.maintenancesUser.findIndex((m) => m.id === maintenanceId)
          if (idxUser >= 0) {
            this.maintenancesUser[idxUser] = archivedMaintenance
          }
          const idxList = this.maintenances.findIndex((m) => m.id === maintenanceId)
          if (idxList >= 0) {
            this.maintenances[idxList] = archivedMaintenance
          }
          useSuccessStore().setSuccess(['Manutenção desarquivada com sucesso!'])
        }

        if (this.currentMaintenance?.id === maintenanceId) {
          this.currentMaintenance = shouldArchive ? null : archivedMaintenance
        }

        return archivedMaintenance
      } catch (error: unknown) {
        const err = error as { response?: { data?: { errors?: string[] } } }
        errorStore.setErrors('Erro ao arquivar manutenção: ', [
          err?.response?.data?.errors?.[0] || 'Falha ao arquivar manutenção',
        ])
        return null
      }
    },

    setCurrentSensorFilter(sensor: ISensor | null) {
      this.currentSensorFilter = sensor
    },

    setCurrentUserFilter(user: IUser | null) {
      this.currentUserFilter = user
    },

    setCurrentStatusFilter(status: string | null) {
      this.currentStatusFilter = status
    },

    setCurrentOrigimFilter(origim: string | null) {
      this.currentOrigimFilter = origim
    },

    setCurrentPriorityFilter(priority: string | null) {
      this.currentPriorityFilter = priority
    },
    setCurrentDescriptionFilter(description: string | null) {
      this.currentDescriptionFilter = description
    },
    setCurrentActionsFilter(actions_taken: string | null) {
      this.currentActionsFilter = actions_taken
    },

    setCurrentStartDateFilter(start_date: string | null) {
      this.currentStartDateFilter = start_date
    },

    setCurrentEndDateFilter(end_date: string | null) {
      this.currentEndDateFilter = end_date
    },

    setCurrentMaintenanceTypeFilter(maintenance_type: IMaintenanceType | null) {
      this.currentMaintenanceTypeFilter = maintenance_type
    },

    clearMaintenanceReport() {
      this.maintenanceReport = null
      this.maintenanceReportLoaded = false
    },

    async fetchMaintenanceReport(startDate: string, endDate: string) {
      const loadingStore = useLoadingStore()
      const errorStore = useErrorStore()
      try {
        errorStore.clearErrors()
        loadingStore.initLoading()
        const result = await MaintenanceResource.getMaintenanceReport(startDate, endDate)
        this.maintenanceReport = result as IMaintenanceReport
        this.maintenanceReportLoaded = true
      } catch (error: unknown) {
        const err = error as { response?: { data?: { errors?: string[] } } }
        errorStore.setErrors('Erro ao carregar relatório de manutenções: ', [
          err?.response?.data?.errors?.[0] || 'Falha ao carregar relatório de manutenções',
        ])
      } finally {
        loadingStore.removeLoading()
      }
    },
  },
})
